import React, { useEffect, useState } from "react";
import {
    Dialog,
    Box,
    Typography,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "sonner";

/**
 * Assumption:
 * - You have an API endpoint that verifies webhook
 * - Example: POST /webhooks/verify
 * - Returns HTTP 2xx on success
 */
const verifyWebhookPing = async ({ url, secret }) => {
    const res = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Webhook-Secret": secret,
        },
    });

    return res;
};

const AddWebhookDialog = ({ open, onClose, onSubmit, initialData }) => {
    const [url, setUrl] = useState("");
    const [secret, setSecret] = useState("");
    const [events, setEvents] = useState({
        interpreted: false,
        aiGenerated: false,
    });

    const [isVerifying, setIsVerifying] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const isEditMode = Boolean(initialData);

    const hasInterpreted =
        !!initialData?.events?.REPORT_INTERPRETED;

    const hasAI =
        !!initialData?.events?.AI_GENERATED_REPORT;


    /* ================= PREFILL (EDIT MODE) ================= */
    useEffect(() => {
        if (!initialData) {
            resetForm();
            return;
        }

        setUrl(initialData.url || "");
        setSecret(initialData.secret || "");

        setIsVerified(true);

        const newEvents = {
            interpreted: false,
            pdfFile: false,
            dataPoints: false,
            aiGenerated: false,
            aiGeneratedPdfFile: false,
            aiGeneratedDataPoints: false,
        };

        const backendEvents = initialData.events || {};

        // ----- INTERPRETED REPORT -----
        if (backendEvents.REPORT_INTERPRETED) {
            newEvents.interpreted = true;

            if (typeof backendEvents.REPORT_INTERPRETED === "object") {
                newEvents.pdfFile = !!backendEvents.REPORT_INTERPRETED.pdf;
                newEvents.dataPoints = !!backendEvents.REPORT_INTERPRETED.data;
            }
        }

        // ----- AI GENERATED REPORT -----
        if (backendEvents.AI_GENERATED_REPORT) {
            newEvents.aiGenerated = true;

            if (typeof backendEvents.AI_GENERATED_REPORT === "object") {
                newEvents.aiGeneratedPdfFile = !!backendEvents.AI_GENERATED_REPORT.pdf;
                newEvents.aiGeneratedDataPoints = !!backendEvents.AI_GENERATED_REPORT.data;
            }
        }

        setEvents(newEvents);
    }, [initialData, open]);



    const resetForm = () => {
        setUrl("");
        setSecret("");
        setEvents({ interpreted: false, aiGenerated: false });
        setIsVerified(false);
    };

    /* ================= VERIFY ================= */
    const handleVerify = async () => {
        if (!url || !secret) return;

        try {
            setIsVerifying(true);
            const res = await verifyWebhookPing({ url, secret });

            if (res.status >= 200 && res.status < 300) {
                setIsVerified(true);
            } else {
                setIsVerified(false);
                toast.error("Webhook verification failed");
            }
        } catch (err) {
            console.error(err);
            setIsVerified(true);
            toast.error("Unable to reach webhook URL");
        } finally {
            setIsVerifying(false);
        }
    };

    /* ================= SUBMIT ================= */
    /* ================= SUBMIT ================= */
    const handleSubmit = async () => {
        const eventsPayload = {};

        if (events.interpreted) {
            eventsPayload.REPORT_INTERPRETED =
                !events.pdfFile && !events.dataPoints
                    ? true
                    : {
                        pdf: !!events.pdfFile,
                        data: !!events.dataPoints,
                    };
        }

        if (events.aiGenerated) {
            eventsPayload.AI_GENERATED_REPORT =
                !events.aiGeneratedPdfFile && !events.aiGeneratedDataPoints
                    ? true
                    : {
                        pdf: !!events.aiGeneratedPdfFile,
                        data: !!events.aiGeneratedDataPoints,
                    };
        }

        const payload = {
            url,
            secret,
            events: eventsPayload,
            signature: { enabled: false },
        };

        const res = await onSubmit(payload);

        if (res?.success === true) {
            toast.success(`${res.message}`);
        }
        if (res?.success === false) {
            toast.error(`${res.message}`);

        }
    };



    const isInterpretedInvalid =
        events.interpreted &&
        !events.pdfFile &&
        !events.dataPoints;

    const isAiInvalid =
        events.aiGenerated &&
        !events.aiGeneratedPdfFile &&
        !events.aiGeneratedDataPoints;

    const isCreateDisabled =
        !url ||
        !secret ||
        !isVerified ||
        (!events.interpreted && !events.aiGenerated) ||
        isInterpretedInvalid ||
        isAiInvalid;



    return (
        <Dialog
            open={open}
            onClose={() => {
                resetForm();     // reset on close
                onClose();
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: "20px", p: 3 } }}
        >
            {/* HEADER */}
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography fontWeight={700} fontSize="20px">
                    {initialData ? "Edit Webhook" : "New Webhook"}
                </Typography>
                <IconButton
                    onClick={() => {
                        resetForm();
                        onClose();
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* FORM */}
            <Box display="flex" flexDirection="column" gap={2}>
                {/* URL */}
                <Box>
                    <Typography fontSize={15} color="var(--app-text-muted)">
                        Webhook URL *
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="https://client.com/webhooks/interpreted"
                        value={url}
                        onChange={(e) => {
                            setUrl(e.target.value);
                            setIsVerified(false);
                        }}
                    />
                </Box>

                {/* SECRET */}
                <Box>
                    <Typography fontSize={15} color="var(--app-text-muted)">
                        Secret *
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="whsec_xxxxx"
                        value={secret}
                        onChange={(e) => {
                            setSecret(e.target.value);
                            setIsVerified(false);
                        }}
                    />

                    {/* VERIFY */}
                    <Button
                        onClick={handleVerify}
                        disabled={!url || !secret || isVerifying}
                        sx={{
                            mt: 1.5,
                            width: 120,
                            backgroundColor: "var(--app-unread)",
                            color: "#5751E9",
                            fontWeight: 600,
                            boxShadow: "none",
                        }}
                    >
                        {isVerifying ? (
                            <CircularProgress size={18} />
                        ) : isVerified ? (
                            "Verified ✓"
                        ) : (
                            "Verify"
                        )}
                    </Button>
                </Box>

                {/* EVENTS */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography fontWeight={600} mb={1}>
                        Required for :
                    </Typography>

                    {/* ===== INTERPRETED REPORT ===== */}
                    {(!isEditMode || hasInterpreted) && (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={events.interpreted}
                                        disabled={isEditMode || !isVerified}
                                        onChange={(e) =>
                                            setEvents((prev) => ({
                                                ...prev,
                                                interpreted: e.target.checked,
                                                // reset children when unchecked
                                                pdfFile: e.target.checked ? prev.pdfFile : false,
                                                dataPoints: e.target.checked ? prev.dataPoints : false,
                                            }))
                                        }
                                    />
                                }
                                label="Doctor Interpreted Report"
                            />

                            {events.interpreted && (
                                <Box sx={{ ml: 4, mt: 0.5 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={events.pdfFile}
                                                onChange={(e) =>
                                                    setEvents({ ...events, pdfFile: e.target.checked })
                                                }
                                            />
                                        }
                                        label="PDF File"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={events.dataPoints}
                                                onChange={(e) =>
                                                    setEvents({ ...events, dataPoints: e.target.checked })
                                                }
                                            />
                                        }
                                        label="Data Points"
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {/* ===== AI GENERATED REPORT ===== */}
                    {(!isEditMode || hasAI) && (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={events.aiGenerated}
                                        disabled={isEditMode || !isVerified}
                                        onChange={(e) =>
                                            setEvents((prev) => ({
                                                ...prev,
                                                aiGenerated: e.target.checked,
                                                aiGeneratedPdfFile: e.target.checked
                                                    ? prev.aiGeneratedPdfFile
                                                    : false,
                                                aiGeneratedDataPoints: e.target.checked
                                                    ? prev.aiGeneratedDataPoints
                                                    : false,
                                            }))
                                        }
                                    />
                                }
                                label="AI Generated Report"
                            />

                            {events.aiGenerated && (
                                <Box sx={{ ml: 4, mt: 0.5 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={events.aiGeneratedPdfFile}
                                                onChange={(e) =>
                                                    setEvents({
                                                        ...events,
                                                        aiGeneratedPdfFile: e.target.checked,
                                                    })
                                                }
                                            />
                                        }
                                        label="PDF File"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={events.aiGeneratedDataPoints}
                                                onChange={(e) =>
                                                    setEvents({
                                                        ...events,
                                                        aiGeneratedDataPoints: e.target.checked,
                                                    })
                                                }
                                            />
                                        }
                                        label="Data Points"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>



                {/* SUBMIT */}
                <Button
                    fullWidth
                    variant="contained"
                    disabled={isCreateDisabled}
                    onClick={handleSubmit}
                    sx={{
                        mt: 2,
                        borderRadius: "12px",
                        height: 44,
                        backgroundColor: "#5751E9",
                        fontWeight: 600,
                        "&:hover": { backgroundColor: "#433AC7" },
                    }}
                >
                    {initialData ? "Update Webhook" : "Create Webhook"}
                </Button>
            </Box>
        </Dialog>
    );
};

export default AddWebhookDialog;
